import React, { useRef, useEffect, useCallback } from "react";
// import { Icons } from '@curves/ui-base';        // 切換按鈕用
// import { FloatButton } from "@Local/Components"; // 切換按鈕用
import htmlFrameFitMode from "@Local/Measurement/data/htmlFrameFitMode";

/**
 * 將 HTML 頁面嵌入 iframe 並提供「最適寬度 / 實際大小」切換功能。
 *
 * Props:
 *   title            - iframe 的 title 屬性（無障礙用途）
 *   pageUrl          - 要載入的頁面 URL
 *   floatButtonClass - 切換按鈕的 CSS class（預設 'secondary-button'，空字串表示不顯示）
 *   defaultDisplayMode - 初始顯示模式（預設 FitToWidth）
 */
const HtmlFrameComponent = props => {
    const { title, pageUrl, defaultDisplayMode = htmlFrameFitMode.FitToWidth } = props;
    // const { floatButtonClass = "secondary-button" } = props; // 切換按鈕用

    // isOriginal: true = 實際大小模式，false = 最適寬度模式
    // const [isOriginal, setIsOriginal] = React.useState(false); // 切換按鈕用
    // const [buttonClass, setButtonClass] = React.useState("");   // 切換按鈕用

    // 開發用診斷訊息，顯示於頁面底部 debug bar（每行一個字串）
    // const [debugMsg, setDebugMsg] = React.useState([]);

    const iframeRef = useRef(null);

    // 記錄 iframe 內容的原始尺寸，供縮放計算使用
    const iframeContentSize = useRef({ width: 0, height: 0, maxElementWidth: 0 });

    // iframe onLoad 觸發：初始化尺寸後套用預設顯示模式
    const handleFrameLoad = () => {
        const iframe = iframeRef.current;

        if (iframe && iframe.contentWindow) {
            initFrameSize();

            defaultDisplayMode === htmlFrameFitMode.FitToWidth ? handleFitToWidth() : handleFitToOriginal();
        }
    };

    // 初始化 iframe 內部佈局：
    //   1. 修正 Android WebView Font Inflation
    //   2. 將所有子元素包入置中 flex wrapper，並記錄最大內容寬度
    const initFrameSize = () => {
        const iframe = iframeRef.current;

        if (!iframe || !iframe.contentWindow) return;

        let doc;

        try {
            doc = iframe.contentWindow.document;
        } catch (e) {
            // cross-origin iframe 直接跳過
            return;
        }

        injectFontInflationFix(iframe);

        iframe.style.width = `100%`;
        iframe.style.height = `${doc.body.scrollHeight + 20}px`;

        const bodyElement = doc.body;

        bodyElement.style.margin = `0px`;
        bodyElement.style.padding = `0px`;

        iframeContentSize.current.width = bodyElement.offsetWidth;
        iframeContentSize.current.height = bodyElement.offsetHeight;

        const wrapper = document.createElement("div");

        wrapper.style.display = "flex";
        wrapper.style.flexDirection = "column";
        wrapper.style.alignItems = "center";

        const children = Array.from(bodyElement.children);

        let elementWidth = 0;

        for (let dom of children) {
            if (dom.offsetWidth > elementWidth) elementWidth = dom.offsetWidth;

            // dom.style.boxShadow = '2px 2px 8px 0 rgba(0, 0, 0, 0.2)';

            wrapper.appendChild(dom);
        }

        bodyElement.appendChild(wrapper);

        iframeContentSize.current.maxElementWidth = elementWidth;

        // if (floatButtonClass !== "") { // 切換按鈕用
        //     setButtonClass(floatButtonClass);
        // }
    };

    /**
     * Android WebView Font Inflation 補償
     *
     * 問題：Android WebView 的 textZoom 設定會把頁面所有字體等比放大（例如 ×1.6）。
     *       CSS text-size-adjust 對此無效，因為這是系統層級的縮放，非 CSS 機制。
     *
     * 關鍵行為：getComputedStyle 回傳的 fontSize 已包含膨脹（= 原始值 × ratio），
     *           但設回 inline style 後，WebView 渲染時還會再膨脹一次。
     *           因此補償值必須除以 ratio²，才能讓最終顯示值回到原始大小：
     *             設定值 = computed / ratio²
     *             最終顯示 = 設定值 × ratio = computed / ratio = 原始值 ✓
     */
    const injectFontInflationFix = iframe => {

        if (!iframe) return;

        let doc;
        let win;

        try {
            if (!iframe.contentWindow) return;

            win = iframe.contentWindow;
            doc = win.document;
        } catch (e) {
            // cross-origin iframe 直接跳過
            return;
        }

        // feature detection（避免舊瀏覽器 crash）
        if (
            !doc ||
            !doc.body ||
            !doc.querySelectorAll ||
            !win.getComputedStyle ||
            typeof Map === "undefined"
        ) {
            return;
        }

        try {

            // 量測膨脹比例：注入已知字體大小的隱藏元素，與實際 computed 比較
            const testEl = doc.createElement("div");
            testEl.style.cssText = "font-size: 16px;";
            doc.body.appendChild(testEl);
            const actual = parseFloat(win.getComputedStyle(testEl).fontSize);
            doc.body.removeChild(testEl);

            const ratio = actual / 16;

            if (ratio > 1) {
                const ratioSq = ratio * ratio;

                // 延遲執行，確保 WebView 字體處理完畢後再讀取 computed 值
                setTimeout(() => {
                    const allElements = doc.querySelectorAll("*");

                    // Pass 1: 先全部讀取（DOM 尚未被修改，避免父元素寫入影響子元素繼承值）
                    const fontSizeMap = new Map();
                    allElements.forEach(el => {
                        const computed = parseFloat(win.getComputedStyle(el).fontSize);
                        if (computed > 0) {
                            fontSizeMap.set(el, computed);
                        }
                    });

                    // Pass 2: 統一寫入補償值
                    fontSizeMap.forEach((computed, el) => {
                        el.style.fontSize = computed / ratioSq + "px";
                    });

                    // 驗證用測試元素（注意：此新元素不在修正迴圈內，仍會被膨脹，屬正常現象）
                    // const testEl2 = doc.createElement('div');
                    // testEl2.style.cssText = 'font-size:16px;';
                    // doc.body.appendChild(testEl2);
                    // const afterFix = parseFloat(win.getComputedStyle(testEl2).fontSize);
                    // doc.body.removeChild(testEl2);

                    // setDebugMsg([
                    //  `ratio: ${ratio.toFixed(3)}, ratio²: ${ratioSq.toFixed(3)}`,
                    //  `per-element fix applied (computed/ratio²)`,
                    //  `verify 16px → ${afterFix}px`,
                    //  `body computed: ${win.getComputedStyle(doc.body).fontSize}`,
                    // ]);
                }, 500);
            }
        } catch (e) {
            // 保護整體流程不中斷
        }
    };

    // 切換至「實際大小」（scale = 1）
    const handleFitToOriginal = useCallback(
        () => {
            const iframe = iframeRef.current;

            if (iframe && iframe.contentWindow) {
                setFrameScale(1);
                // setIsOriginal(true); // 切換按鈕用
            }
        },
        // eslint-disable-next-line
        [],
    );

    // 切換至「最適寬度」：依 iframe 可視寬度與內容最大寬度計算 scale
    const handleFitToWidth = useCallback(
        () => {
            const iframe = iframeRef.current;

            if (iframe && iframe.contentWindow) {
                const iframeW = iframe.offsetWidth;
                const elementW = iframeContentSize.current.maxElementWidth;
                let scale = iframeW / elementW;

                // 限制縮放範圍，避免過大或過小
                if (scale > 3) scale = 3;
                if (scale < 0.1) scale = 0.1;

                setFrameScale(scale);
                // setIsOriginal(false); // 切換按鈕用
            }
        },
        // eslint-disable-next-line
        [],
    );

    /**
     * 對 iframe body 套用 CSS transform scale，並同步更新 iframe 高度。
     * 縮放原點為左上角（transformOrigin: left top），
     * body 寬度需補償至「至少等於原始內容寬度」以防止內容被截斷。
     */
    const setFrameScale = scale => {
        const iframe = iframeRef.current;

        if (!iframe || !iframe.contentWindow) return;

        let doc;

        try {
            doc = iframe.contentWindow.document;
        } catch (e) {
            // cross-origin iframe 直接跳過
            return;
        }

        const bodyElement = doc.body;
        const wrapper = bodyElement.children[0];

        bodyElement.style.transform = `scale(${scale})`;
        bodyElement.style.transformOrigin = "left top";

        const iframeW = iframe.offsetWidth;
        const elementW = iframeContentSize.current.maxElementWidth;
        // scale < 1 時 body 需要放寬，才不會讓縮小後的內容被裁切
        const newBodyW = iframeW / scale > elementW ? iframeW / scale : elementW;

        bodyElement.style.width = `${newBodyW}px`;
        wrapper.style.width = `${newBodyW}px`;

        const contentH = iframeContentSize.current.height;
        const scaleH = bodyElement.scrollHeight * scale;

        // scale < 1：以縮放後的視覺高度為準；scale >= 1：維持原始內容高度
        if (scale < 1) {
            bodyElement.style.height = `${scaleH}px`;
        } else {
            bodyElement.style.height = `${contentH}px`;
        }

        iframe.style.height = `${scaleH + 20}px`;
    };

    // 監聽裝置旋轉，重新計算最適寬度
    useEffect(
        () => {
            const handleOrientationChange = () => {
                handleFitToWidth();
            };

            window.addEventListener("orientationchange", handleOrientationChange);

            return () => {
                window.removeEventListener("orientationchange", handleOrientationChange);
            };
        },
        // eslint-disable-next-line
        [handleFitToWidth],
    );

    return (
        <React.Fragment>
            {/* 開發診斷 bar：固定於畫面底部，顯示 Font Inflation 偵測與修正結果 */}
            {/* {debugMsg.length > 0 && (
				<div
					style={{
						position: "fixed",
						bottom: 0,
						left: 0,
						right: 0,
						background: "rgba(0,0,0,0.82)",
						color: "#0f0",
						fontSize: "13px",
						padding: "6px 10px",
						zIndex: 9999,
					}}
				>
					{debugMsg.map((line, i) => (
						<div key={i} style={{ wordBreak: "break-all" }}>
							{line}
						</div>
					))}
				</div>
			)} */}
            <iframe ref={iframeRef} title={title} src={pageUrl} onLoad={handleFrameLoad} />
            {/* 暫時隱藏最適寬度 / 實際大小切換按鈕
            {isOriginal ? (
                <FloatButton tip="最適寬度" className={buttonClass} onClick={handleFitToWidth}>
                    <Icons.ResizeFitToWidth htmlColor={"#fff"} />
                </FloatButton>
            ) : (
                <FloatButton tip="實際大小" className={buttonClass} onClick={handleFitToOriginal}>
                    <Icons.ResizeOriginal htmlColor={"#fff"} />
                </FloatButton>
            )}
            */}
        </React.Fragment>
    );
};

export default HtmlFrameComponent;
